import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Flask, CheckCircle, Warning, LinkSimple, LinkBreak, CaretDown, CaretRight, ArrowRight } from "@phosphor-icons/react";

const fmtNum = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
const btn = "h-9 px-3 rounded-sm text-[10px] font-mono-num tracking-widest border transition-colors disabled:opacity-40 flex items-center gap-1.5";
const th = "text-left px-2 py-1.5";
const KIND_TONE = { duplicate: "text-cyan-300", service: "text-violet-300", to_confirm: "text-amber-300", sheet: "text-slate-300", master: "text-orange-300" };

export default function SheetMasterPanel({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [report, setReport] = useState(null);
  const [masters, setMasters] = useState([]);
  const [choices, setChoices] = useState({});
  const [dryKey, setDryKey] = useState(null);
  const [busy, setBusy] = useState("");

  const loadMeta = useCallback(async () => {
    try {
      const [b, m] = await Promise.all([api.get("/admin/backup/log"), api.get("/items/sheet-masters")]);
      setLastBackup(b.data?.[0] || null);
      setMasters(m.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { if (open) loadMeta(); }, [open, loadMeta]);

  const backup = async () => {
    setBusy("backup");
    try {
      const { data } = await api.get("/admin/backup/full", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `shobhit-full-backup-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.json.gz`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Full backup downloaded");
      loadMeta();
    } catch (e) { toast.error("Backup fail"); } finally { setBusy(""); }
  };

  const run = async (dry) => {
    if (!dry && !window.confirm("APPLY karein? Sirf naye fields jude ge (link, service, to-confirm, metal/thickness). Naam, stock, price, category nahi badlenge.")) return;
    setBusy(dry ? "dry" : "apply");
    try {
      const { data } = await api.post(`/admin/migrations/sheet-master?dry_run=${dry}`, { masters: choices });
      setReport(data);
      setDryKey(dry ? JSON.stringify(choices) : null);
      toast.success(dry ? "Dry-run report ready" : `Applied · ${data.items_to_update} items updated`);
      if (!dry) { loadMeta(); onChanged?.(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const backupFresh = lastBackup && (Date.now() - new Date(lastBackup.at).getTime()) < 24 * 3600 * 1000;
  const dryCurrent = report?.dry_run && dryKey === JSON.stringify(choices);

  return (
    <div className="surface rounded-sm border-l-4 border-cyan-500" data-testid="sheet-master-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4" data-testid="sheet-master-toggle">
        <div className="text-left">
          <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">// SHEET MASTER · ADMIN ONLY</div>
          <div className="font-heading text-lg font-black text-white">Sheet items ek jagah — duplicates link karo</div>
        </div>
        {open ? <CaretDown size={18} className="text-slate-400" /> : <CaretRight size={18} className="text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={backup} disabled={!!busy} className={`${btn} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10`} data-testid="sheet-backup-btn">
              <DownloadSimple size={13} /> {busy === "backup" ? "DOWNLOADING…" : "1 · FULL BACKUP"}
            </button>
            <button onClick={() => run(true)} disabled={!!busy} className={`${btn} border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10`} data-testid="sheet-dryrun-btn">
              <Flask size={13} /> {busy === "dry" ? "RUNNING…" : "2 · DRY-RUN REPORT"}
            </button>
            <button onClick={() => run(false)} disabled={!!busy || !backupFresh || !dryCurrent}
              className={`${btn} border-orange-500 bg-orange-500/20 text-orange-200 hover:bg-orange-500/30`} data-testid="sheet-apply-btn">
              <CheckCircle size={13} /> {busy === "apply" ? "APPLYING…" : "3 · APPLY"}
            </button>
            <span className="text-[10px] font-mono-num text-slate-500" data-testid="sheet-last-backup">
              LAST BACKUP: {lastBackup ? new Date(lastBackup.at).toLocaleString("en-IN") : "NONE"}
              {!backupFresh && " · APPLY ke liye 24 ghante ke andar ka backup chahiye"}
              {report?.dry_run && !dryCurrent && " · MASTER CHOICE BADLI — DRY-RUN DOBARA CHALAO"}
            </span>
          </div>
          {report && <MigrationReport report={report} choices={choices} setChoices={setChoices} />}
          <MasterList masters={masters} onChanged={() => { loadMeta(); onChanged?.(); }} />
        </div>
      )}
    </div>
  );
}

function MigrationReport({ report, choices, setChoices }) {
  return (
    <div className="space-y-4" data-testid="sheet-migration-report">
      <div className={`text-xs font-mono-num tracking-widest flex items-center gap-2 flex-wrap ${report.totals_match ? "text-emerald-400" : "text-red-400"}`}
        data-testid="sheet-totals-match">
        {report.totals_match ? <CheckCircle size={14} /> : <Warning size={14} />}
        {report.dry_run ? "DRY-RUN" : "APPLIED"} · {report.items_to_update} ITEMS {report.dry_run ? "WILL BE " : ""}UPDATED ·
        TOTALS {report.totals_match ? "MATCH ✓" : "MISMATCH ✗"}
        {report.snapshot && <span className="text-slate-500"> · SNAPSHOT {report.snapshot}</span>}
      </div>
      <MappingTable mapping={report.mapping || []} choices={choices} setChoices={setChoices} />
      <div className="grid md:grid-cols-3 gap-3">
        <MiniList title="SERVICE (sheet nahi)" tone="text-violet-300" rows={report.services} testid="sheet-services" />
        <MiniList title="TO CONFIRM (kuch nahi bhara)" tone="text-amber-300" rows={report.to_confirm} testid="sheet-to-confirm" />
        <MiniList title="RULE NAAM NAHI MILA" tone="text-red-300" rows={(report.rules_not_found || []).map((n) => ({ name: n }))} testid="sheet-rules-not-found" />
      </div>
      <ChangesTable changes={report.changes || []} />
      <BeforeAfter report={report} />
    </div>
  );
}

function MappingTable({ mapping, choices, setChoices }) {
  return (
    <div className="overflow-x-auto" data-testid="sheet-mapping">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-1">// DUPLICATE → MASTER MAPPING</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr><th className={th}>GROUP</th><th className={th}>OLD / DUPLICATE ITEMS</th><th className={th}></th><th className={th}>MASTER</th><th className={th}>START WEIGHT</th></tr>
        </thead>
        <tbody>
          {mapping.map((m) => (
            <tr key={m.group} className="border-t border-slate-800 align-top" data-testid={`sheet-map-${m.group.replace(/\s+/g, "-")}`}>
              <td className="px-2 py-2 font-mono-num text-orange-300 whitespace-nowrap">{m.group}</td>
              <td className="px-2 py-2 text-slate-200">
                {m.duplicates.length ? m.duplicates.map((d) => (
                  <div key={d.item_id}>{d.name} <span className="text-slate-500 font-mono-num">· {fmtNum(d.current_stock, 3)} {d.unit}{d.already_linked_to ? " · ALREADY LINKED" : ""}</span></div>
                )) : <span className="text-slate-500">—</span>}
              </td>
              <td className="px-1 py-2 text-slate-500"><ArrowRight size={12} /></td>
              <td className="px-2 py-2">
                {m.candidates.length > 1 ? (
                  <select value={choices[m.group] || ""} onChange={(e) => setChoices({ ...choices, [m.group]: e.target.value })}
                    className="h-8 bg-slate-800 border border-amber-500/50 text-amber-200 text-[11px] rounded-sm px-2"
                    data-testid={`sheet-master-choice-${m.group.replace(/\s+/g, "-")}`}>
                    <option value="">MASTER CHUNO…</option>
                    {m.candidates.map((c) => <option key={c.item_id} value={c.item_id}>{c.name}</option>)}
                  </select>
                ) : m.master ? <span className="text-emerald-300">{m.master.name}</span>
                  : <span className="text-red-300 font-mono-num text-[10px]">MASTER NAHI MILA — LINK NAHI HOGA</span>}
                {m.candidates.length > 1 && !m.master && <div className="text-[10px] text-amber-300 font-mono-num mt-1">CHUNNE TAK LINK NAHI HOGA</div>}
                {m.candidates.length > 1 && m.master && <div className="text-[10px] text-slate-500 mt-1">Baaki candidates bhi is master se link honge</div>}
              </td>
              <td className="px-2 py-2 font-mono-num text-slate-300 whitespace-nowrap">{m.start_sheet_weight_kg ? `${m.start_sheet_weight_kg} KG / 32 SQFT` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiniList({ title, tone, rows, testid }) {
  return (
    <div className="surface-2 p-3 rounded-sm" data-testid={testid}>
      <div className={`text-[10px] font-mono-num tracking-widest mb-1 ${tone}`}>{title} ({rows?.length || 0})</div>
      {(rows || []).map((r, i) => <div key={r.item_id || i} className="text-xs text-slate-300">{r.name}</div>)}
      {!rows?.length && <div className="text-xs text-slate-500">—</div>}
    </div>
  );
}

function ChangesTable({ changes }) {
  if (!changes.length) return null;
  return (
    <div className="overflow-x-auto">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-1">// FIELDS TO ADD (SIRF KHAALI FIELDS)</div>
      <table className="w-full text-xs">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr><th className={th}>ITEM</th><th className={th}>TYPE</th><th className={th}>NEW FIELDS</th></tr>
        </thead>
        <tbody>
          {changes.map((c) => (
            <tr key={c.item_id} className="border-t border-slate-800" data-testid={`sheet-change-${c.item_id}`}>
              <td className="px-2 py-1.5 text-slate-200">{c.name}</td>
              <td className={`px-2 py-1.5 font-mono-num text-[10px] tracking-widest ${KIND_TONE[c.kind] || ""}`}>{c.kind.toUpperCase()}</td>
              <td className="px-2 py-1.5 font-mono-num text-slate-400">
                {c.link_to ? <span className="text-cyan-300">LINK → {c.link_to}</span>
                  : Object.entries(c.set).map(([k, v]) => `${k}=${typeof v === "number" ? fmtNum(v, 3) : v}`).join(" · ")}
                {c.metal_assumed && <span className="text-amber-300"> · METAL NAAM ME NAHI — MS MAANA</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BeforeAfter({ report }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="sheet-before-after">
        <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
          <tr><th className={th}>CHECK</th><th className="text-right px-2 py-1.5">BEFORE</th><th className="text-right px-2 py-1.5">AFTER</th></tr>
        </thead>
        <tbody>
          {Object.keys(report.before || {}).map((k) => {
            const same = report.before[k] === report.after[k];
            return (
              <tr key={k} className="border-t border-slate-800">
                <td className="px-2 py-1 text-slate-400 font-mono-num">{k}</td>
                <td className="px-2 py-1 text-right font-mono-num text-slate-200">{fmtNum(report.before[k])}</td>
                <td className={`px-2 py-1 text-right font-mono-num ${same ? "text-emerald-400" : "text-red-400"}`}>{fmtNum(report.after[k])}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StockLog({ item, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/items/${item.id}/ledger`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [item.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="stock-log-dialog">
      <div className="surface rounded-sm p-4 w-full max-w-4xl max-h-[85vh] overflow-auto">
        <div className="flex justify-between items-center mb-3">
          <div className="font-heading font-black text-white">Stock Log · {item.name}</div>
          <button onClick={onClose} className="text-xs text-slate-300 border border-slate-600 px-3 h-8 rounded-sm" data-testid="stock-log-close">CLOSE</button>
        </div>
        {!data ? <div className="text-slate-500 text-sm">Loading…</div> : (
          <table className="w-full text-xs">
            <thead className="text-[10px] font-mono-num tracking-widest text-slate-500 bg-slate-800/50">
              <tr><th className={th}>DATE</th><th className="text-right px-2">± SQFT</th><th className="text-right px-2">BALANCE</th><th className={th}>REASON</th><th className={th}>REF NO.</th><th className={th}>BY</th></tr>
            </thead>
            <tbody>
              {data.events.length === 0 && <tr><td colSpan="6" className="text-center py-4 text-slate-500 italic">Koi movement nahi.</td></tr>}
              {[...data.events].reverse().map((e) => (
                <tr key={e.id} className="border-t border-slate-800" data-testid={`stock-log-row-${e.id}`}>
                  <td className="px-2 py-1.5 font-mono-num whitespace-nowrap">{new Date(e.created_at).toLocaleString("en-IN")}</td>
                  <td className={`px-2 py-1.5 text-right font-mono-num ${e.direction === "IN" ? "text-emerald-400" : "text-red-400"}`}>{e.direction === "IN" ? "+" : "−"}{fmtNum(e.quantity, 3)}</td>
                  <td className="px-2 py-1.5 text-right font-mono-num">{fmtNum(e.balance, 3)}</td>
                  <td className="px-2 py-1.5 text-slate-300">{e.movement_type}{e.note ? ` · ${e.note}` : ""}</td>
                  <td className="px-2 py-1.5 font-mono-num text-orange-300">{e.reference_no || "—"}</td>
                  <td className="px-2 py-1.5 text-slate-400">{e.created_by_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MasterList({ masters, onChanged }) {
  const [logOf, setLogOf] = useState(null);
  const unlink = async (id) => {
    try { await api.post(`/items/${id}/unlink-master`); toast.success("Unlinked"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!masters.length) return <div className="text-slate-500 italic text-sm">Koi sheet item nahi.</div>;
  return (
    <div className="space-y-2" data-testid="sheet-master-list">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// CURRENT SHEET MASTERS ({masters.length})</div>
      {masters.map((m) => (
        <div key={m.id} className="surface-2 p-3 rounded-sm" data-testid={`sheet-master-${m.id}`}>
          <div className="text-slate-100 font-semibold flex items-center justify-between gap-2">
            <span>{m.name}
              {m.to_confirm && <span className="ml-2 text-[10px] font-mono-num text-amber-300 tracking-widest">TO CONFIRM</span>}
            </span>
            <button onClick={() => setLogOf(m)} className="text-[10px] font-mono-num tracking-widest text-cyan-300 border border-cyan-500/40 px-2 h-7 rounded-sm" data-testid={`stock-log-btn-${m.id}`}>STOCK LOG</button>
          </div>
          {m.avg_cost_per_sqft != null && (
            <div className="text-[10px] font-mono-num text-emerald-300 tracking-widest" data-testid={`sheet-avg-cost-${m.id}`}>
              AVG ₹{fmtNum(m.avg_rate_per_kg, 2)}/KG · {fmtNum(m.avg_sheet_weight_kg, 3)} KG/SHEET · ₹{fmtNum(m.avg_cost_per_sqft, 2)}/SQFT
            </div>
          )}
          <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">
            {m.group || "GROUP —"} · WEIGHT {m.start_sheet_weight_kg ? `${m.start_sheet_weight_kg} KG/SHEET` : "PURCHASE SE"} ·
            STOCK {Object.entries(m.combined_stock || {}).map(([u, v]) => `${fmtNum(v, 3)} ${u}`).join(" + ")}
            {m.linked_items?.length > 0 && <span className="text-slate-600"> · Old linked balance: {fmtNum(m.old_linked_balance, 3)} (not counted)</span>}
          </div>
          {logOf?.id === m.id && <StockLog item={m} onClose={() => setLogOf(null)} />}
          {m.linked_items.map((c) => (
            <div key={c.id} className="mt-2 flex items-center justify-between text-xs border-t border-slate-800 pt-2" data-testid={`sheet-linked-${c.id}`}>
              <span className="text-slate-300 flex items-center gap-1"><LinkSimple size={12} className="text-cyan-400" /> {c.name}
                <span className="text-slate-500 font-mono-num"> · {fmtNum(c.current_stock, 3)} {c.unit}</span></span>
              <button onClick={() => unlink(c.id)} className="text-[10px] font-mono-num text-red-300 flex items-center gap-1" data-testid={`sheet-unlink-${c.id}`}>
                <LinkBreak size={12} /> UNLINK
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
